import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";

import { createEditorSession, updateWidgetActions } from "actions/editor";

import { 
  fetchGating, updateGating, restoreGating, invalidateGatingState 
} from "actions/gating";

import _ from 'lodash';
import cx from 'classnames';
import { toast } from "react-toastify";

import { Editor } from "components/Editor";
import { Loader } from "components/ApplicationLoader";
import StoryboardContext from "components/Storyboard/context";
import { PageHeader, HeaderContext } from "../components/header";
import { SuccessToast, FailureToast } from "components/Toast";

import { FORM_SUBMIT, MODAL, SLIDE } from "constants/widgetTypes";
import { useDisableFormSubmitHyperlink } from "components/Editor/widgets/form/submit";
import { getPageViewedEventPayload } from "services/mixpanelUtil";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_EDITOR } from "constants/mixpanelEvents";
import { fetchIntegrations } from "actions/integrations/integrations";
import { SLIDE_STORYBOARDS } from "constants/storyboardType";

import styles from './gating.module.scss';

export default function GatingView(props) {
  const dispatch = useDispatch();

  const { id } = useParams();
  const editorSessionId = `editors_gating_${id}`;
  const editorSession = React.useRef(editorSessionId);

  const [headerContext, setHeaderContext] = useState({
    type: 'gating',
    entity: {},
    editorSession: null,
    updateState: {},
    onSave: () => {},
    onRestore: () => {}
  });

  const gating = useSelector((state) => _.get(state, `gatings.gating.${id}.item`));
  const editorWidgets = useSelector((state) => _.get(state, `${editorSessionId}.present.widgets`));
  const disableFormSubmitLink = useDisableFormSubmitHyperlink(editorSessionId);

  const gatingLoaded = _.get(gating, "id");
  const loadingGating = useSelector(state => _.get(state, `gatings.gating.${id}.loading`, false));

  const updateState = useSelector((state) => _.get(state, `gatings.gating.${id}.update`));

  const restoreState = useSelector((state) => _.get(state, `gatings.gating.${id}.restore`));

  const storyboard = _.get(gating, "storyboard");
  const storyboardId = _.get(storyboard, "id");
  const storyboardName = _.get(storyboard, "name");
  const storyboardType = _.get(storyboard, "type");

  const themes = _.get(storyboard, 'themes', []);
  const brandTheme = _.get(storyboard, 'brandTheme', []);
  const appearance = _.get(storyboard, 'appearance', {});

  const reference = {
    type: 'gating',
    id: id
  }

  useEffect(() => {
    if (!disableFormSubmitLink || !editorWidgets?.length) {
      return;
    }

    editorWidgets.forEach((widget) => {
      if (widget.type !== FORM_SUBMIT || !widget.actions?.length) {
        return;
      }

      dispatch(updateWidgetActions(editorSessionId, widget.uid, []));
    });
  }, [disableFormSubmitLink, editorWidgets]);

  useEffect(() => {
    dispatch(fetchGating(id, false));
    dispatch(fetchIntegrations());
    editorSession.current = `editors_gating_${id}`;
  }, [id])

  useEffect(() => {
    if(gatingLoaded){
      var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_EDITOR, storyboardId, storyboardName, storyboardType)
      mixPanelPayload = {
        ...mixPanelPayload,
        editor_type: 'Gating',
        device_type: 'Desktop'
      }
      dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
    }
  }, [storyboard]);

  useEffect(() => {
    if (editorSession.current === `editors_gating_${gatingLoaded}` && !loadingGating) {
      initialiseEditor();
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: gating,
          editorSession: editorSession.current,
          updateState: updateState,
          onSave: handleGatingSave,
          onRestore: handleGatingRestore
        }
      })
    }
  }, [editorSession.current, gatingLoaded, loadingGating]);

  useEffect(() => {
    if (gating) {
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: gating,
        }
      })
    }
  }, [gating]);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        updateState: updateState
      }
    })

    if (!updateState) {
      return;
    }

    if (updateState.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${gating.name} saved successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateGatingState(gating.id));
    } else if (updateState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to save ${gating.name} gating`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateGatingState(gating.id));
    }
  }, [updateState])

  useEffect(() => {
    if (restoreState && restoreState.success) {
      initialiseEditor();
    }

    if (!restoreState) {
      return;
    }

    if (restoreState.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${gating.name} restored successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateGatingState(gating.id));
    } else if (restoreState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to restore ${gating.name} gating`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidateGatingState(gating.id));
    }
  }, [restoreState]);

  const initialiseEditor = () => {
    const desktopGuides = _.get(gating, "draft.desktop.guides");
    const mobileGuides = _.get(gating, "draft.mobile.guides");
    const widgets = _.get(gating, "draft.widgets") || [];
    const fonts = _.get(gating, "draft.fonts") || [];
    const modal = widgets.find(item => item.type === MODAL);
    const slide = widgets.find(item => item.type === SLIDE);


    const initialState = {
      desktop: {
        guides: desktopGuides
      },
      mobile: {
        guides: mobileGuides
      },
      widgets: widgets,
      interactive: {
        active: SLIDE_STORYBOARDS.includes(storyboardType) ? _.get(slide, 'uid') : _.get(modal, 'uid')
      },
      entityType:'GATING',
      themes: themes,
      appearance: appearance,
      brandTheme: brandTheme,
      fonts: fonts
    };

    dispatch(createEditorSession(editorSession.current, initialState));
  }

  const handleGatingSave = () => {
    dispatch(updateGating(gating.id, editorSession.current));
  }

  const handleGatingRestore = () => {
    dispatch(restoreGating(gating.id, editorSession.current));
  }

  const updateOptimzeLayout = () => {
    setHeaderContext(headerContext => ({
      ...headerContext,
      optimizeLayout: true
    }));
  
    setTimeout(() => {
      setHeaderContext(headerContext => ({
        ...headerContext,
        optimizeLayout: false
      }));
    }, 1000);
  };

  return (
    <StoryboardContext.Provider value={storyboard}>
      <div className={cx(styles.gating, { [styles.loadingGating]: !gatingLoaded })}>
        <Loader className={cx(styles.pageLoader, { [styles.loadingGating]: !gatingLoaded })} title="Loading Editor..." />
        <HeaderContext.Provider value={headerContext}>
          <PageHeader editorSession={editorSession.current} gating={gating} reference={reference} updateOptimzeLayout={updateOptimzeLayout} />
          <Editor key={editorSession.current} id={editorSession.current} entityType="gating" />
        </HeaderContext.Provider>
      </div>
    </StoryboardContext.Provider>
  );
}